import * as React from 'react';
import * as ContractRepository from '../../../Repository/ContractRepository';
import { Button } from 'antd';
import message from 'antd/es/message';
import { UserRoles } from '../../../Repository/UserRepository';
import { formatUTCDateFromDateString } from "../../../Helper/DateHelper";
import { Contract, Tree, ContractUser, User } from 'tree-wallet-client';
import { isAllowed } from '../../../Service/AuthService';
import { isMobileDevice } from '../../../Helper/DeviceHelper';
import { SHORT_MESSAGE_SECONDS, TREE_ID_COPIED, TREE_ID_COPY_FAILED } from '../../../Helper/BannerMessages';


// ---------------------------------------------------------------------------------------------------
// Tree popup descriptions layout: all numbers & fields can be edited here
// ---------------------------------------------------------------------------------------------------

// Air above and below every field (`padding` in the rules below)
const FIELD_PADDING_Y = 5;
// Space between a label and its value when they share a line
const LABEL_GAP = 10;
// A label's line box: antd's 14px text at its own 1.5714 line-height
const LABEL_LINE_HEIGHT = 22;
// Height of the glyphs themselves, i.e. what is left of that box once its leading is taken out
const LABEL_GLYPH_HEIGHT = 14;
// Half the leading: how far the first label is lifted so its glyphs start on the top edge of the field
const FIRST_LABEL_LIFT = (LABEL_LINE_HEIGHT - LABEL_GLYPH_HEIGHT) / 2;


interface TreeDescriptionProps {
  tree: Tree;
  user: User;
  language: string;
  contract?: Contract;
  isValidated: boolean;
}

/** One line of the description: a label and what it holds */
interface Field {
  label: string;
  value: React.ReactNode;
  /** Button drawn at the right of the label, on the label's own line (the tree id's Copy) */
  labelAction?: React.ReactNode;
  /** Stays selectable on Mobile, where the rest of the details block is not (see `renderField`) */
  selectable?: boolean;
}

const containerStyle: React.CSSProperties = {
  padding: `${FIELD_PADDING_Y}px 0px`,
}

const labelStyle = {
  fontWeight: 'bold',
  marginRight: `${LABEL_GAP}px`
};

// The planting hash is one 32-character word: it has to break rather than widen its column
const contentStyle: React.CSSProperties = {
  overflowWrap: 'break-word',
};

// On a phone a long press selects the word under the finger and then drags the whole block into the
// selection: the details block takes itself out, and only the fields marked `selectable` opt back in
const notSelectableStyle: React.CSSProperties = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

const selectableStyle: React.CSSProperties = {
  userSelect: 'text',
  WebkitUserSelect: 'text',
  WebkitTouchCallout: 'default',
};

function getTranslation(tree: Tree, language: string) {
  try {
    return tree.treeType.translations[language]
      ? tree.treeType.translations[language]
      : tree.treeType.translations.latin;
  } catch (err) {
    return tree.treeType.translations.latin;
  }
}


/**
 * Copies a text to the clipboard. `navigator.clipboard` needs a secure context and a user gesture, and
 * still refuses now and then (the Android shell's WebView included), so the old textarea trick is kept
 * as a second path.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.warn('Clipboard API refused, falling back to the textarea copy', err);
  }

  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(field);
    return copied;
  } catch (err) {
    console.warn('Could not copy to the clipboard', err);
    return false;
  }
}
export default function TreeDescription(props: TreeDescriptionProps) {
  const { tree, language, isValidated, contract, user } = props;
  // One answer for the whole render: the long-press behaviour below is the phone's
  const mobile = isMobileDevice();
  const translation = getTranslation(tree, language);
  const latinTranslation = getTranslation(tree, 'latin');

  const getCustomer = () => {
    const customers = contract?.contractUsers?.filter((cu: any) => {
      return cu.roles.indexOf(ContractRepository.roles.Customer.name) > -1;
    });

    if (customers && customers.length >= 1) {
      return customers;
    }

    return undefined;
  };

  const calculateCo2Compensation = (plantingDate: string) => {
    const plantingDateObj = new Date(plantingDate);
    const nowDateObj = new Date();
    const timeDiff = nowDateObj.getTime() - plantingDateObj.getTime();
    const daysDiff = (timeDiff / 3600) / 24;
    const co2Compensation = (daysDiff / 365.25) * 22;
    return parseFloat((co2Compensation / 1000).toFixed(2));
  };

  // const getValidationLabel = (tree: Tree) => {
  //   if (!isValidated) {
  //     //   return <Tag icon={<ExclamationCircleOutlined/>} color="red">TO BE VALIDATED</Tag>
  //     // } else
  //     if (_.findIndex(getValidatedTrees(), { 'id': String(tree.id) }) > -1) {
  //       return <Tag color="orange">QUEUED</Tag>
  //     } else {
  //       return <Tag color="green">{tree.validationHash ? tree.validationHash : "VALIDATED"}</Tag>
  //     }
  //   }
  // };

  const customers = getCustomer();
  const timeDate = formatUTCDateFromDateString(tree.createdAt);

  const copyTreeId = async () => {
    const copied = await copyToClipboard(String(tree.id));
    if (copied) {
      message.success({ content: TREE_ID_COPIED, duration: SHORT_MESSAGE_SECONDS });
    } else {
      message.error(TREE_ID_COPY_FAILED);
    }
  };

  // The lines are collected first and laid out afterwards
  const fields: Field[] = [
    {
      label: 'Tree ID',
      value: tree.id,
      labelAction: <Button size="small" onClick={copyTreeId}>Copy</Button>,
    },
    {
      label: 'Description',
      value: <>
        {`${tree.numberPlanted}x ${translation}`}
        <span style={{ display: latinTranslation === translation ? 'none' : 'inline', marginLeft: '5px' }}>({latinTranslation})</span>
      </>,
    },
    { label: 'Type', value: tree.treeType?.fruitTree ? 'fruit tree' : 'shade tree' },
    { label: 'Position accuracy', value: `${tree.accuracy} meter${Math.floor(tree.accuracy) > 1 ? 's' : ''}` },
    {
      label: 'Altitude',
      value: tree.altitude === 0
        ? '-'
        : `${Math.round(tree.altitude)} meter${Math.abs(Math.round(tree.altitude)) > 1 ? 's' : ''}`,
    },
  ];

  if (tree.altitude_accuracy !== undefined && tree.altitude_accuracy !== null) {
    fields.push({
      label: 'Altitude accuracy',
      value: `${Math.floor(tree.altitude_accuracy)} meter${Math.abs(Math.floor(tree.altitude_accuracy)) > 1 ? 's' : ''}`,
    });
  }

  if (isAllowed(user.roles, [UserRoles.Admin])) {
    fields.push({ label: 'Contract', value: `${tree.contract?.description} (ID ${tree.contract?.id})` });
  }

  fields.push({ label: 'Planted by', value: `${tree.user.firstName} ${tree.user.lastName}` });
  fields.push({ label: 'Date', value: `${timeDate.date} - ${timeDate.time} (UTC)` });

  if (tree.plantedHash) {
    fields.push({ label: 'Planting hash', value: tree.plantedHash });
  }

  if (isValidated) {
    fields.push({ label: 'Stored CO2', value: `${calculateCo2Compensation(tree.createdAt)} Kg` });
  }

  if (customers) {
    fields.push({
      label: 'Sponsored by',
      value: customers.map((customer: ContractUser) => (
        <div key={'tree_customer'}>
          {customer?.user?.company
            ? customer?.user?.company
            : `${customer?.user?.firstName} ${customer?.user?.lastName}`}
        </div>
      )),
    });
  }

  const renderField = (field: Field, index: number) => {
    // The first field sits level with the top of the photo. Its label keeps the line box of every other
    // label - so the gap down to the value is the same - and is lifted by the leading that box leaves
    // above its glyphs
    const first = index === 0;

    return (
      <div
        key={field.label}
        style={{
          ...(mobile ? { ...containerStyle, ...notSelectableStyle } : containerStyle),
          ...(first ? { paddingTop: 0 } : {}),
        }}
      >
        <div
          style={{
            ...labelStyle,
            // A label carrying a button becomes a row of its own, the button at its right. 
            // The two are aligned on their text baselines
            ...(field.labelAction ? { display: 'flex', alignItems: 'baseline', gap: `${LABEL_GAP}px` } : {}),
            ...(first ? { marginTop: -FIRST_LABEL_LIFT } : {}),
          }}
        >
          <span>{field.label}</span>
          {field.labelAction}
        </div>
        <div style={mobile && field.selectable ? { ...contentStyle, ...selectableStyle } : contentStyle}>
          {field.value}
        </div>
      </div>
    );
  };

  return <>
    {fields.map(renderField)}
  </>
}
